import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Divider,
} from '@mui/material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SecurityIcon from '@mui/icons-material/Security';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import SetTotp from './SetTotp';
import SetWebauthn from './SetWebauthn';
import useAppStore from '@/store/app';
import { useTheme } from '@emotion/react';

export default function MfaOptions({ onSetupSuccess, onSetupFailure }) {
  const [selectedOption, setSelectedOption] = useState(null);
  const { setDialogForm } = useAppStore();
  const theme = useTheme();

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    if (option === 'totp') {
      setDialogForm(
        <SetTotp
          onSetupSuccess={onSetupSuccess}
          onSetupFailure={onSetupFailure}
          setSelectedOption={setSelectedOption}
        />,
      );
    }
    if (option === 'webauthn') {
      setDialogForm(
        <SetWebauthn
          onSetupSuccess={onSetupSuccess}
          onSetupFailure={onSetupFailure}
          setSelectedOption={setSelectedOption}
        />,
      );
    }
  };

  if (selectedOption) {
    return null;
  }

 return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        width: '100%',
      }}
    >
      <Card
        sx={{
          flex: 1,
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
          border: `1px solid ${theme.palette.primary.light}`,
        }}
      >
        <CardActionArea
          onClick={() => handleOptionSelect('totp')}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <PhoneAndroidIcon sx={{ fontSize: 24, mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Authenticator App (TOTP)
            </Typography>
          </Box>

          <CardContent
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              '&:last-child': { pb: 2 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 1,
                p: 1,
                color: theme.palette.primary.main,
              }}
            >
              <VpnKeyIcon sx={{ fontSize: 60 }} />
            </Box>

            <Typography variant="body2" paragraph sx={{ mb: 1 }}>
              Verwenden Sie eine Authenticator App auf Ihrem Smartphone für
              zeitbasierte Einmalcodes.
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Geeignet für:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Nutzer mit einem Smartphone
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Einfache Einrichtung und Nutzung
            </Typography>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="small"
              sx={{ mt: 'auto' }}
              onClick={() => handleOptionSelect('totp')}
            >
              TOTP einrichten
            </Button>
          </CardContent>
        </CardActionArea>
      </Card>

      <Card
        sx={{
          flex: 1,
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
          border: `1px solid ${theme.palette.secondary.light}`,
        }}
      >
        <CardActionArea
          onClick={() => handleOptionSelect('webauthn')}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.secondary.main,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <SecurityIcon sx={{ fontSize: 24, mr: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Biometrie/Security Key
            </Typography>
          </Box>

          <CardContent
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              '&:last-child': { pb: 2 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: 1,
                p: 1,
                color: theme.palette.secondary.main,
              }}
            >
              <FingerprintIcon sx={{ fontSize: 60 }} />
            </Box>

            <Typography variant="body2" paragraph sx={{ mb: 1 }}>
              Nutzen Sie Biometrie (Fingerabdruck/Gesicht) oder einen
              physischen Sicherheitsschlüssel.
            </Typography>

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Geeignet für:
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Höchste Sicherheitsanforderungen
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              • Geräte mit Biometrie-Scanner
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              fullWidth
              size="small"
              sx={{ mt: 'auto' }}
              onClick={() => handleOptionSelect('webauthn')}
            >
              WebAuthn einrichten
            </Button>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  );
}